/**
 * Roteador do Lumma Store.
 *
 * `RotaPrivada` esconde a tela de quem não tem sessão — e isso é UX, não
 * segurança. A CONSTITUICAO.md §4.1 é explícita: checagem na camada de
 * renderização protege a tela, não a ação. Toda escrita é decidida pelo
 * porteiro no servidor, a cada requisição.
 */
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import type { ReactNode } from 'react'

import { BarraDeAvisos } from './componentes/BarraDeAvisos'
import { TelaLogin } from './modulos/auth/TelaLogin'
import { TelaCriarConta } from './modulos/auth/TelaCriarConta'
import {
  TelaAceitarConvite,
  TelaConfirmarEmail,
  TelaRedefinirSenha,
} from './modulos/auth/TelasDeToken'
import { TelaOnboarding } from './modulos/onboarding/TelaOnboarding'
import { TelaDashboard } from './modulos/app/TelaDashboard'
import { TelaNaoEncontrada } from './modulos/app/TelaNaoEncontrada'
import { TelaEstoque } from './modulos/estoque/TelaEstoque'
import { TelaClientes } from './modulos/clientes/TelaClientes'
import {
  TelaCadastroCliente,
  TelaEdicaoCliente,
} from './modulos/clientes/TelaCadastroCliente'
import { TelaFornecedores } from './modulos/fornecedores/TelaFornecedores'
import {
  TelaCadastroFornecedor,
  TelaEdicaoFornecedor,
} from './modulos/fornecedores/TelaCadastroFornecedor'
import { TelaProdutos } from './modulos/produtos/TelaProdutos'
import { TelaCadastroProduto } from './modulos/produtos/TelaCadastroProduto'
import { TelaEdicaoProduto } from './modulos/produtos/TelaEdicaoProduto'
import { TelaEtiquetas } from './modulos/etiquetas/TelaEtiquetas'
import { TelaPDV } from './modulos/pdv/TelaPDV'
import { TelaCondicionais } from './modulos/condicional/TelaCondicionais'
import { TelaAbrirCondicional } from './modulos/condicional/TelaAbrirCondicional'
import { TelaFaturarCondicional } from './modulos/condicional/TelaFaturarCondicional'
import { TelaCaixa } from './modulos/caixa/TelaCaixa'
import { TelaHistoricoTurnos } from './modulos/caixa/TelaHistoricoTurnos'
import { TelaTurno } from './modulos/caixa/TelaTurno'
import { TelaListaDeNotasFiscais } from './modulos/notasfiscais/TelaListaDeNotasFiscais'
import { TelaImportarNotaFiscal } from './modulos/notasfiscais/TelaImportarNotaFiscal'
import { TelaRevisarNotaFiscal } from './modulos/notasfiscais/TelaRevisarNotaFiscal'
import { TelaRelatorios } from './modulos/relatorios/TelaRelatorios'
import { TelaFinanceiro } from './modulos/financeiro/TelaFinanceiro'
import {
  TelaEdicaoLancamento,
  TelaNovoLancamento,
} from './modulos/financeiro/TelaLancamento'
import { TelaFiscal } from './modulos/fiscal/TelaFiscal'
import { TelaConfiguracoes } from './modulos/configuracoes/TelaConfiguracoes'
import { TelaDadosDaEmpresa } from './modulos/configuracoes/TelaDadosDaEmpresa'
import { TelaUsuarios } from './modulos/configuracoes/TelaUsuarios'
import { TelaPerfis } from './modulos/configuracoes/TelaPerfis'
import { TelaMeuPerfil } from './modulos/conta/TelaMeuPerfil'
import { ProvedorDeSessao } from './plataforma/sessao'
import { useSessao } from './plataforma/contexto-sessao'

export function App() {
  return (
    <BrowserRouter>
      <ProvedorDeSessao>
        <Rotas />
        {/* Fora das rotas: o aviso sobrevive à navegação. "Cliente salvo"
            aparece DEPOIS de a tela já ter voltado para a listagem — se
            morasse dentro da tela que salvou, desmontaria junto com ela e
            ninguém veria. */}
        <BarraDeAvisos />
      </ProvedorDeSessao>
    </BrowserRouter>
  )
}

function Rotas() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to="/entrar" replace />} />

      <Route path="/entrar" element={<TelaLogin />} />
      <Route path="/criar-conta" element={<TelaCriarConta />} />
      <Route path="/confirmar-email" element={<TelaConfirmarEmail />} />
      <Route path="/redefinir-senha" element={<TelaRedefinirSenha />} />
      <Route path="/aceitar-convite" element={<TelaAceitarConvite />} />

      <Route
        path="/primeiro-acesso"
        element={
          <RotaPrivada>
            <TelaOnboarding />
          </RotaPrivada>
        }
      />
      <Route
        path="/app"
        element={
          <RotaPrivada>
            <TelaDashboard />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/estoque"
        element={
          <RotaPrivada>
            <TelaEstoque />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/produtos"
        element={
          <RotaPrivada>
            <TelaProdutos />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/produtos/novo"
        element={
          <RotaPrivada>
            <TelaCadastroProduto />
          </RotaPrivada>
        }
      />
      {/* Antes do coringa `:codigo` pela mesma razão de `/novo`: "etiquetas"
          não é o código de um produto. */}
      <Route
        path="/app/produtos/etiquetas"
        element={
          <RotaPrivada>
            <TelaEtiquetas />
          </RotaPrivada>
        }
      />
      {/* O parâmetro é o CÓDIGO do produto, nunca o uuid (CONSTITUICAO.md §2).
          Fica depois de `/novo` porque "novo" não é um código. */}
      <Route
        path="/app/produtos/:codigo/editar"
        element={
          <RotaPrivada>
            <TelaEdicaoProduto />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/clientes"
        element={
          <RotaPrivada>
            <TelaClientes />
          </RotaPrivada>
        }
      />
      {/* Cadastro e edição de cliente são ROTAS, e não modos de `useState`
          dentro da listagem — mesma razão de produtos: formulário sem endereço
          perde tudo num F5 e não volta pelo botão do navegador.

          `/novo` vem antes do parâmetro porque "novo" não é um código. */}
      <Route
        path="/app/clientes/novo"
        element={
          <RotaPrivada>
            <TelaCadastroCliente />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/clientes/:codigo/editar"
        element={
          <RotaPrivada>
            <TelaEdicaoCliente />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/fornecedores"
        element={
          <RotaPrivada>
            <TelaFornecedores />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/fornecedores/novo"
        element={
          <RotaPrivada>
            <TelaCadastroFornecedor />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/fornecedores/:codigo/editar"
        element={
          <RotaPrivada>
            <TelaEdicaoFornecedor />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/pdv"
        element={
          <RotaPrivada>
            <TelaPDV />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/condicionais"
        element={
          <RotaPrivada>
            <TelaCondicionais />
          </RotaPrivada>
        }
      />
      {/* `/novo` vem antes do parâmetro dinâmico de faturar, mesmo critério de
          `/app/clientes/novo` — "novo" não é um código de condicional. */}
      <Route
        path="/app/condicionais/novo"
        element={
          <RotaPrivada>
            <TelaAbrirCondicional />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/condicionais/:codigo/faturar"
        element={
          <RotaPrivada>
            <TelaFaturarCondicional />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/caixa"
        element={
          <RotaPrivada>
            <TelaCaixa />
          </RotaPrivada>
        }
      />
      {/* O histórico é rota própria, e não uma aba do turno: "quanto tem na
          gaveta agora" e "o turno de terça bateu" são perguntas de momentos
          diferentes, e a segunda precisa de filtro e paginação. */}
      <Route
        path="/app/caixa/turnos"
        element={
          <RotaPrivada>
            <TelaHistoricoTurnos />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/caixa/turnos/:codigo"
        element={
          <RotaPrivada>
            <TelaTurno />
          </RotaPrivada>
        }
      />
      {/* Compras é a nota fiscal de compra. `/app/compras` continua existindo
          só para não quebrar link antigo — quem chega nela cai na listagem. */}
      <Route path="/app/compras" element={<Navigate to="/app/compras/notas-fiscais" replace />} />
      <Route
        path="/app/compras/notas-fiscais"
        element={
          <RotaPrivada>
            <TelaListaDeNotasFiscais />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/compras/notas-fiscais/importar"
        element={
          <RotaPrivada>
            <TelaImportarNotaFiscal />
          </RotaPrivada>
        }
      />
      {/* A revisão é rota própria, e não um estado da tela de upload: ela é
          onde a decisão de gravar acontece, tem URL para onde voltar e não
          pode desaparecer porque alguém remontou o componente do upload. */}
      <Route
        path="/app/compras/notas-fiscais/importar/revisar"
        element={
          <RotaPrivada>
            <TelaRevisarNotaFiscal />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/relatorios"
        element={
          <RotaPrivada>
            <TelaRelatorios />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/financeiro"
        element={
          <RotaPrivada>
            <TelaFinanceiro />
          </RotaPrivada>
        }
      />
      {/* O lançamento manual é TELA, e não gaveta: doze campos atrás de um Esc
          que descarta sem aviso é armadilha. O lado (receber/pagar) viaja na
          busca — `?lado=pagar`. */}
      <Route
        path="/app/financeiro/lancamentos/novo"
        element={
          <RotaPrivada>
            <TelaNovoLancamento />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/financeiro/lancamentos/:numero/editar"
        element={
          <RotaPrivada>
            <TelaEdicaoLancamento />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/fiscal"
        element={
          <RotaPrivada>
            <TelaFiscal />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/configuracoes"
        element={
          <RotaPrivada>
            <TelaConfiguracoes />
          </RotaPrivada>
        }
      />
      {/* A empresa tem rota curta porque é destino do bloco do topo, que está
          em toda tela — e o perfil, porque é destino do menu do usuário. As
          duas moram conceitualmente em Configurações, mas um caminho de três
          níveis para o clique mais frequente do cabeçalho é atrito à toa. */}
      <Route
        path="/app/empresa"
        element={
          <RotaPrivada>
            <TelaDadosDaEmpresa />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/perfil"
        element={
          <RotaPrivada>
            <TelaMeuPerfil />
          </RotaPrivada>
        }
      />
      {/* Usuários e Perfis moram conceitualmente em Configurações — chegam
          aqui pelos cartões da aba "Usuários e perfis", com trilha de volta
          (CONSTITUICAO.md §4.1: guardado no servidor por `users.manage`,
          o front só esconde a rota do menu). */}
      <Route
        path="/app/usuarios"
        element={
          <RotaPrivada>
            <TelaUsuarios />
          </RotaPrivada>
        }
      />
      <Route
        path="/app/perfis"
        element={
          <RotaPrivada>
            <TelaPerfis />
          </RotaPrivada>
        }
      />

      {/*
        Endereço desconhecido DENTRO do app não expulsa ninguém.

        Isto era `Navigate to="/entrar"` para tudo, e o efeito era assustador:
        clicar num link para uma tela que ainda não existe jogava a pessoa na
        tela de login, como se a sessão tivesse caído. Quem está autenticado vê
        que o endereço não existe e volta; quem não está continua indo para o
        login, que é o certo para ele.
      */}
      <Route path="*" element={<RotaDesconhecida />} />
    </Routes>
  )
}

function RotaDesconhecida() {
  const { situacao } = useSessao()

  if (situacao.estado === 'carregando') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-texto-suave">Carregando…</p>
      </div>
    )
  }

  if (situacao.estado === 'anonimo') {
    return <Navigate to="/entrar" replace />
  }

  return <TelaNaoEncontrada />
}

function RotaPrivada({ children }: { children: ReactNode }) {
  const { situacao } = useSessao()

  if (situacao.estado === 'carregando') {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p className="text-sm text-texto-suave">Carregando…</p>
      </div>
    )
  }

  if (situacao.estado === 'anonimo') {
    return <Navigate to="/entrar" replace />
  }

  return <>{children}</>
}
